import React, { useState, useEffect, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import {
	Panel,
	Text,
	Margin,
	ContentLoader,
} from 'shared/components'
import { useInfiniteScroll } from 'shared/hooks'

import MobileBeneficiaryCard from '../mobileBeneficiaryCard/component'
import EmptyFavoritesState from '../emptyFavoritesState/component'
import { MobileHeader } from '../mobileHeader'
import { BottomNavigation } from 'routes/public/events/overview/components/bottomNavigation'
import * as styles from './styles'

const MobileBeneficiariesLayoutComponent = ({
	loadBeneficiaries,
	loadFavoriteBeneficiaries,
	loadCategories,
	onSearch,
	followerService,
}) => {
	const [activeTab, setActiveTab] = useState('all')
	const [categories, setCategories] = useState([])
	const [beneficiariesData, setBeneficiariesData] = useState({
		content: [],
		loading: false,
	})
	const [pagination, setPagination] = useState({
		page: 0,
		hasMore: true,
		loadingMore: false,
	})
	// Track followed beneficiaries locally
	const [followedBeneficiaries, setFollowedBeneficiaries] = useState(new Set())

	// Ref to track current pagination state to avoid race conditions
	const paginationRef = useRef(pagination)

	// Ref to track current active tab to prevent race conditions
	const activeTabRef = useRef(activeTab)

	// Ref to cancel ongoing requests
	const abortControllerRef = useRef(null)

	// Update ref whenever pagination changes
	useEffect(() => {
		paginationRef.current = pagination
	}, [pagination])

	// Update ref whenever activeTab changes
	useEffect(() => {
		activeTabRef.current = activeTab
	}, [activeTab])

	const loadInitialData = async () => {
		// Cancel any ongoing request
		if (abortControllerRef.current) {
			abortControllerRef.current.abort()
		}

		// Create new abort controller for this request
		abortControllerRef.current = new AbortController()

		// Store the tab this request is for
		const requestTab = activeTabRef.current



		setBeneficiariesData((prev) => ({ ...prev, loading: true }))
		setPagination({ page: 0, hasMore: true, loadingMore: false })

		try {
			let result
			if (requestTab === 'favorite') {
				result = await loadFavoriteBeneficiaries({ page: 0, pageSize: 20 })
			} else {
				const query = getQueryForTab(requestTab)
				result = await loadBeneficiaries(
					{ page: 0, pageSize: 20 },
					null,
					query
				)
			}

			// CRITICAL: Check if tab changed during the API call
			if (activeTabRef.current !== requestTab) {
				return
			}

			const content = result?.content || []

			// Update local followed state based on API response
			const newFollowedIds = new Set()
			content.forEach(item => {
				const id = item.id || item.beneficiaryDTO?.id
				const isFollowed = item.followed || item.beneficiaryDTO?.followed
				if (isFollowed && id) {
					newFollowedIds.add(id)
				}
			})
			setFollowedBeneficiaries(prev => {
				// Merge with existing followed IDs (in case API doesn't return all)
				const merged = new Set([...prev, ...newFollowedIds])
				return merged
			})



			setBeneficiariesData({ content, loading: false })

			// Has more items if API returned full page size
			const hasMoreItems = (content.length || 0) === 20

			setPagination((prev) => ({
				...prev,
				page: 0,
				hasMore: hasMoreItems,
			}))
		} catch (error) {
			setBeneficiariesData((prev) => ({ ...prev, loading: false }))
		}
	}

	const getQueryForTab = (tab) => {
		if (tab === 'all') {
			return {}
		}
		// Find category by description (Healthcare, Education, etc.)
		const category = categories.find(
			(cat) => cat.description.toLowerCase() === tab.toLowerCase()
		)

		const query = category ? { categories: [category.category] } : {}


		// API expects array of category IDs, not full objects
		return query
	}

	const loadMoreData = useCallback(async () => {
		const currentPagination = paginationRef.current

		if (currentPagination.loadingMore || !currentPagination.hasMore) {
			return
		}

		// CRITICAL: Double-check activeTab to prevent race conditions
		if (activeTabRef.current === 'favorite') {
			return
		}

		const nextPage = currentPagination.page + 1

		// Immediately set loading to prevent duplicate calls
		setPagination((prev) => ({ ...prev, loadingMore: true }))

		try {
			let result

			if (activeTab === 'favorite') {
				result = await loadFavoriteBeneficiaries({
					page: nextPage,
					pageSize: 20,
				})
			} else {
				const query = getQueryForTab(activeTab)
				result = await loadBeneficiaries(
					{ page: nextPage, pageSize: 20 },
					null,
					query
				)
			}

			const newContent = result?.content || []

			setBeneficiariesData((prev) => {
				const existingIds = new Set(prev.content.map((item) => item.id))
				const uniqueNewContent = newContent.filter(
					(item) => !existingIds.has(item.id)
				)

				return {
					...prev,
					content: [...prev.content, ...uniqueNewContent],
				}
			})

			// Has more items if API returned full page size
			const hasMoreItems = newContent.length === 20

			setPagination((prev) => ({
				...prev,
				page: nextPage,
				hasMore: hasMoreItems,
				loadingMore: false,
			}))
		} catch (error) {
			setPagination((prev) => ({ ...prev, loadingMore: false }))
		}
	}, [activeTab, loadBeneficiaries, loadFavoriteBeneficiaries])

	// Load categories and initialize followed beneficiaries
	useEffect(() => {
		const loadInitialSetup = async () => {
			try {
				// Load categories
				const categoriesData = await loadCategories()
				setCategories(categoriesData || [])

				// Load all followed beneficiaries to populate local state
				const favoritesResponse = await loadFavoriteBeneficiaries({ page: 0, pageSize: 1000 })
				const favoritesBeneficiaries = favoritesResponse?.content || []

				const followedIds = new Set()
				favoritesBeneficiaries.forEach(fav => {
					const id = fav.beneficiaryDTO?.id || fav.id
					if (id) {
						followedIds.add(id)
					}
				})
				setFollowedBeneficiaries(followedIds)

			} catch (error) {
				// Setup failed silently
			}
		}
		loadInitialSetup()
	}, [loadCategories, loadFavoriteBeneficiaries])

	// Load initial data when tab changes
	useEffect(() => {
		if (categories.length > 0 || activeTab === 'all' || activeTab === 'favorite') {
			loadInitialData()
		}
	}, [activeTab, categories])

	useInfiniteScroll({
		enabled: activeTab !== 'favorite',
		getPagination: () => paginationRef.current,
		getContentLength: () => beneficiariesData.content.length,
		loadMoreData,
	})

	const handleTabChange = (tabId) => {
		if (tabId !== activeTab) {
			setActiveTab(tabId)
		}
	}

	const handleFavoriteToggle = async (beneficiary, isCurrentlyFollowed) => {
		try {
			const beneficiaryId = beneficiary.beneficiaryDTO?.id || beneficiary.id
			// Immediately update local state for instant UI feedback
			setFollowedBeneficiaries(prev => {
				const newSet = new Set(prev)
				if (isCurrentlyFollowed) {
					newSet.delete(beneficiaryId)
				} else {
					newSet.add(beneficiaryId)
				}
				return newSet
			})

			if (isCurrentlyFollowed) {
				// Remove from favorites
				await followerService.removeFollowers({
					beneficiaryIds: [beneficiaryId]
				})
			} else {
				// Add to favorites
				await followerService.addFollowers({
					beneficiaryIds: [beneficiaryId]
				})
			}

			// Refresh the current data
			loadInitialData()
		} catch (error) {
			// Revert local state on error
			setFollowedBeneficiaries(prev => {
				const newSet = new Set(prev)
				if (isCurrentlyFollowed) {
					newSet.add(beneficiaryId) // Add back if removal failed
				} else {
					newSet.delete(beneficiaryId) // Remove if addition failed
				}
				return newSet
			})
		}
	}

	const renderContent = () => {
		if (beneficiariesData.loading) {
			return <ContentLoader />
		}

		if (activeTab === 'favorite' && beneficiariesData.content.length === 0) {
			return <EmptyFavoritesState />
		}



		return (
			<div css={styles.cardsContainer}>
				{beneficiariesData.content.map((beneficiary) => (
					<MobileBeneficiaryCard
						key={beneficiary.id}
						beneficiary={beneficiary}
						isFavorite={activeTab === 'favorite'}
						isFollowed={followedBeneficiaries.has(beneficiary.id || beneficiary.beneficiaryDTO?.id)}
						activeFilter={activeTab}
						onFavoriteToggle={handleFavoriteToggle}
					/>
				))}
				{beneficiariesData.content.length === 0 && !beneficiariesData.loading && (
					<div css={styles.emptyState}>
						<Text size={16} gray>
							No beneficiaries found
						</Text>
					</div>
				)}
				{pagination.loadingMore && (
					<div css={styles.loadingMore}>
						<ContentLoader waiting={true}>
							<div css={styles.loadingPlaceholder}>
								<Text size={14} gray>
									Loading more beneficiaries...
								</Text>
							</div>
						</ContentLoader>
					</div>
				)}
			</div>
		)
	}

	return (
		<>
			<Panel>
				<MobileHeader
					title="Beneficiaries"
					activeTab={activeTab}
					categories={categories}
					onTabChange={handleTabChange}
				/>



				<div css={styles.contentContainer}>
					<ContentLoader waiting={beneficiariesData.loading || false}>
						{renderContent()}
					</ContentLoader>
				</div>
			</Panel>
			<BottomNavigation />
		</>
	)
}

MobileBeneficiariesLayoutComponent.propTypes = {
	loadBeneficiaries: PropTypes.func.isRequired,
	loadFavoriteBeneficiaries: PropTypes.func.isRequired,
	loadCategories: PropTypes.func.isRequired,
	onSearch: PropTypes.func.isRequired,
	followerService: PropTypes.object.isRequired,
}

export default MobileBeneficiariesLayoutComponent
